import { grey } from '@mui/material/colors';
import { Popup } from 'maplibre-gl';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import BikeStationPopup from '../../components/map/bike-station-popup';
import { bikeStationIcons } from '../../models';
import { createImageMarker, getAnchorAndOffset, getLeastOverlappingPopupAnchor, popupTipSize } from '../../utils';
import { useLayers } from './layers';
import { usePopup } from './popup';
import { arrivalLayerId, wayPointsLayerId } from './routing';
import { useSource } from './source';
const sourceId = 'bike-stations';
const layerId = 'bike-stations';
const attribution = '<a href="https://github.com/eskerda/pybikes" target="_blank" rel="noreferrer">pybikes</a>';
const asMarkerMinZoom = 14.5;
const routeTooltipOffset = 16;
export function useBikeStations(map, theme, search, smallDevice, {
  onSearchUpdated
} = {}) {
  const [selectedBikeStation, selectBikeStation] = useState(null);
  const initialized = useRef(false);
  const {
    getGeoJSONSource,
    addGeoJSONSource,
    updateGeoJSONSource,
    clearGeoJSONSource
  } = useSource(map, sourceId);
  const {
    addCircleLayer
  } = useLayers(map);
  const bikeStationsRef = useRef([]);
  const selectedIdRef = useRef(null);
  const markers = useRef({});
  const routeBikeStationsRef = useRef(null);
  const tooltip = useRef(null);
  const snackbar = useSnackbar();
  const {
    t
  } = useTranslation();
  const {
    centerPopup
  } = usePopup(map);
  const placeRouteTooltipsRef = useRef(placeRouteTooltips);
  placeRouteTooltipsRef.current = placeRouteTooltips;
  useEffect(() => {
    if (!map) return;
    const handleViewChange = () => placeRouteTooltipsRef.current();
    map.on('zoomend', handleViewChange);
    map.on('rotateend', handleViewChange);
    return () => {
      map.off('zoomend', handleViewChange);
      map.off('rotateend', handleViewChange);
    };
  }, [map]);
  function init() {
    if (!map || initialized.current) return;
    if (getGeoJSONSource()) {
      initialized.current = true;
      return;
    }
    tooltip.current = new Popup({
      className: 'map-tooltip',
      closeButton: false
    });
    addGeoJSONSource(undefined, {
      type: 'geojson',
      attribution
    });
    addCircleLayer(layerId, sourceId, {
      'circle-radius': 4,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#fff'
    });
    map.on('mouseenter', layerId, event => {
      if (event.defaultPrevented) return;
      event.originalEvent.preventDefault();
      event.preventDefault();
      map.getCanvas().style.cursor = 'pointer';
      const {
        point,
        features
      } = event;
      if (map.queryRenderedFeatures(point).find(({
        layer: {
          id: layerId
        }
      }) => [wayPointsLayerId, arrivalLayerId].indexOf(layerId) !== -1) || !features || !features[0]) return;
      const {
        id,
        geometry,
        properties
      } = features[0];
      if (selectedIdRef.current === id) return;
      if (typeof id === 'number' && geometry.type === 'Point') {
        const {
          coordinates: [lng, lat]
        } = geometry;
        openTooltip(id, {
          lat,
          lng
        }, properties?.title || null, 6);
      }
    });
    map.on('click', layerId, event => {
      if (event.originalEvent.defaultPrevented) return;
      event.originalEvent.preventDefault();
      const {
        features
      } = event;
      if (features && features.length > 0) {
        const {
          id: bikeStationId,
          geometry
        } = features[0];
        if (typeof bikeStationId === 'number') {
          select(bikeStationsRef.current.find(({
            id
          }) => id === bikeStationId) || null);
        }
        if (geometry.type === 'Point') {
          const {
            coordinates: [lng, lat]
          } = geometry;
          map.flyTo({
            center: {
              lat,
              lng
            },
            zoom: asMarkerMinZoom
          });
        }
      }
    });
    map.on('mouseleave', layerId, () => clearTooltip());
    initialized.current = true;
  }
  function openTooltip(id, location, title, _offset = 0) {
    clearTooltip();
    if (!map || !title || selectedIdRef.current === id) return;
    const {
      anchor,
      offset
    } = getAnchorAndOffset(map, location, _offset);
    tooltip.current = new Popup({
      anchor,
      offset,
      className: 'map-tooltip',
      closeButton: false
    }).setHTML(title).setLngLat(location).addTo(map);
  }
  function addMarker(bikeStation) {
    if (!map) return;
    const {
      id,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      },
      label,
      availableBikesPercentage,
      status
    } = bikeStation;
    const popup = new Popup({
      maxWidth: '400px',
      closeButton: false,
      closeOnClick: true,
      className: 'map-custom-popup',
      offset: 12
    }).setHTML(`<div id="bike-station-${id}-popup"></div>`);
    popup.on('open', () => markers.current[id]?.routeTooltip?.remove());
    popup.on('close', () => {
      placeRouteTooltip(id);
      if (selectedIdRef.current === id && markers.current[id]) {
        unselect();
      }
    });
    const routeTooltipContent = status === 'open' ? getRouteTooltipContent(bikeStation) : null;
    const marker = createImageMarker({
      id: `bike-station-marker-${id}`,
      radius: 14,
      borderColor: grey[500],
      borderWidth: 1,
      backgroundColor: '#fff',
      backgroundImage: (status === 'closed' ? bikeStationIcons.closed : bikeStationIcons[availableBikesPercentage]) || '',
      backgroundSize: 28,
      zIndex: selectedIdRef.current === id ? '2' : '1'
    }, {
      onClick: () => {
        clearTooltip();
        if (selectedIdRef.current !== id) {
          selectedIdRef.current = id;
          closePrevPopup();
          selectBikeStation(bikeStation);
          openPopup(bikeStation);
        }
      },
      onMouseEnter: () => {
        if (!routeTooltipContent) openTooltip(id, {
          lat,
          lng
        }, label, 15);
      },
      onMouseLeave: () => clearTooltip()
    }).setLngLat({
      lat,
      lng
    }).setPopup(popup).addTo(map);
    markers.current[id] = {
      id,
      marker,
      routeTooltipContent
    };
    placeRouteTooltip(id);
  }
  function removeMarker(id) {
    const markerItem = markers.current[id];
    if (!markerItem) return;
    delete markers.current[id];
    markerItem.marker.remove();
    markerItem.routeTooltip?.remove();
  }
  function placeRouteTooltip(id) {
    const markerItem = markers.current[id];
    if (!map || !markerItem) return;
    const {
      marker,
      routeTooltipContent
    } = markerItem;
    markerItem.routeTooltip?.remove();
    markerItem.routeTooltip = undefined;
    if (!routeTooltipContent || marker.getPopup().isOpen()) return;
    const location = marker.getLngLat();
    const createRouteTooltip = anchor => new Popup({
      anchor,
      offset: routeTooltipOffset,
      className: 'map-tooltip',
      closeButton: false,
      closeOnClick: false
    }).setHTML(routeTooltipContent).setLngLat(location).addTo(map);
    let routeTooltip = createRouteTooltip('bottom');
    const content = routeTooltip.getElement().querySelector('.maplibregl-popup-content');
    if (content && routeBikeStationsRef.current) {
      const {
        width,
        height
      } = content.getBoundingClientRect();
      const anchor = getLeastOverlappingPopupAnchor(map, location, {
        width,
        height
      }, routeTooltipOffset + popupTipSize, routeBikeStationsRef.current.geometries);
      if (anchor !== 'bottom') {
        routeTooltip.remove();
        routeTooltip = createRouteTooltip(anchor);
      }
    }
    markerItem.routeTooltip = routeTooltip;
  }
  function placeRouteTooltips() {
    Object.keys(markers.current).forEach(id => placeRouteTooltip(Number(id)));
  }
  function getRouteTooltipContent({
    id,
    availableClassicBikes,
    availableEBikes,
    availableLots
  }) {
    if (!routeBikeStationsRef.current) return null;
    const {
      departureId,
      arrivalId,
      eBike
    } = routeBikeStationsRef.current;
    const lines = [];
    if (id === departureId) lines.push(eBike ? t('commons.bike_stations.available_e_bikes', {
      count: availableEBikes
    }) : t('commons.bike_stations.available_bikes', {
      count: availableClassicBikes
    }));
    if (id === arrivalId) lines.push(t('commons.bike_stations.available_slots', {
      count: availableLots
    }));
    return lines.length > 0 ? lines.join('<br />') : null;
  }
  function openPopup(bikeStation) {
    closePrevPopup();
    if (smallDevice) return;
    const {
      id,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      }
    } = bikeStation;
    const selectedMarker = selectedIdRef.current && markers.current[selectedIdRef.current];
    if (!selectedMarker) {
      unselect();
      return;
    }
    if (selectedMarker.marker.getPopup().isOpen()) return;
    selectedMarker.marker.togglePopup();
    const ele = document.getElementById(`bike-station-${id}-popup`);
    if (ele) {
      const container = createRoot(ele);
      container.render(/*#__PURE__*/React.createElement(BikeStationPopup, {
        bikeStation: bikeStation,
        enqueueSnackbar: snackbar?.enqueueSnackbar,
        onClose: () => {
          if (selectedMarker.marker.getPopup().isOpen()) {
            selectedMarker.marker.togglePopup();
            if (container) container.unmount();
          }
        },
        onSearchUpdated: onSearchUpdated,
        onUpdated: updatedBikeStation => {
          const {
            id,
            status,
            availableBikesPercentage
          } = updatedBikeStation;
          const markerItem = markers.current[id];
          if (markerItem?.routeTooltipContent) markerItem.routeTooltipContent = getRouteTooltipContent(updatedBikeStation);
          const ele = document.getElementById(`bike-station-marker-${id}`);
          if (ele) {
            ele.style.backgroundImage = `url(${status === 'closed' ? bikeStationIcons.closed : bikeStationIcons[availableBikesPercentage]})`;
          }
        },
        search: search,
        theme: theme
      }));
    }
    centerPopup({
      lat,
      lng
    }, selectedMarker.marker.getPopup(), {
      height: 400
    });
  }
  function closePrevPopup() {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      if (id !== selectedIdRef.current && marker.getPopup().isOpen()) marker.togglePopup();
    });
  }
  function select(bikeStation) {
    selectedIdRef.current = bikeStation?.id || null;
    selectBikeStation(bikeStation);
    if (!bikeStation || !map) return;
    const {
      location: {
        point: {
          coordinates: [lng, lat]
        }
      }
    } = bikeStation;
    map.flyTo({
      center: {
        lat,
        lng
      },
      zoom: asMarkerMinZoom
    });
  }
  function unselect() {
    selectedIdRef.current = null;
    selectBikeStation(null);
    closePrevPopup();
  }
  function update(bikeStations, {
    forceAsMarkers,
    routeBikeStations
  } = {}) {
    bikeStationsRef.current = bikeStations;
    routeBikeStationsRef.current = routeBikeStations || null;
    if (!map) return;
    const zoom = map.getZoom();
    if (zoom >= asMarkerMinZoom || forceAsMarkers) {
      clearGeoJSONSource();
      const bikeStationsIds = bikeStations.reduce((res, {
        id
      }) => {
        res[id] = true;
        return res;
      }, {});

      // remove markers that are no longer visible
      Object.values(markers.current).filter(({
        id
      }) => !bikeStationsIds[id]).forEach(({
        id
      }) => removeMarker(id));

      // unselect bike station if no longer visible
      if (selectedIdRef.current && !markers.current[selectedIdRef.current]) {
        unselect();
      }

      // add markers that were not visible
      bikeStations.filter(({
        id
      }) => !markers.current[id]).forEach(bikeStation => addMarker(bikeStation));

      // close previous selected bike station popup
      closePrevPopup();

      // open selected bike station popup
      const selectedBikeStation = selectedIdRef.current && bikeStations.find(({
        id
      }) => selectedIdRef.current === id);
      if (selectedBikeStation) openPopup(selectedBikeStation);
    } else {
      clearMarkers();
      unselect();
      updateGeoJSONSource({
        type: 'FeatureCollection',
        features: bikeStations.map(({
          id,
          label,
          location: {
            point
          },
          color
        }) => {
          return {
            id,
            type: 'Feature',
            geometry: point,
            properties: {
              title: label,
              color
            }
          };
        })
      });
    }
  }
  function clearTooltip() {
    if (map) map.getCanvas().style.cursor = '';
    tooltip.current?.remove();
  }
  function clearMarkers(keepSelected = false) {
    Object.values(markers.current).forEach(({
      id
    }) => {
      if (!keepSelected || id !== selectedIdRef.current) removeMarker(id);
    });
  }
  function clear(keepSelected = false) {
    bikeStationsRef.current = bikeStationsRef.current.filter(({
      id
    }) => keepSelected && id === selectedIdRef.current);
    clearMarkers(keepSelected);
    clearGeoJSONSource();
    if (!keepSelected) unselect();
    clearTooltip();
  }
  return {
    initialized: initialized.current,
    selectedBikeStation,
    init,
    update,
    unselect,
    clear
  };
}